// @flow
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import GreyButton from 'esm-web-pro/components/layout/GreyButton';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Box, Text } from 'grommet';
import { getUser, connectAPI, APIContext } from 'speed-js-react';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import connectWS from 'speed-js-react/src/websocket/connectWS';
import WebSocketClient from 'speed-js-core/src/websocket/WebSocketClient';
import { LoginForm } from 'speed-js-react-web';

import { useHistory, useLocation } from 'react-router';
import { removeUser, setPermissions, setUser } from 'speed-js-react/src/redux/auth/actions';
import ConfirmBeforeUnload from 'esm-web-pro/components/dialogs/ConfirmBeforeUnload';
import { getIsLandingTab, setLandingTab } from 'esm-web-pro/redux/landingTab';
import {
    LANDING_TAB_LC_KEY,
    LANDING_TAB_WS_COMMAND,
} from 'esm-web-pro/containers/pages/landing/LandingTabController';
import RedButton from 'esm-web-pro/components/layout/RedButton';
import { normalizeToLocation, resolveToLocation } from 'react-router-dom/modules/utils/locationUtils';
import landingLogo from '../../../assets/images/landingTab/landingLogo.svg';


const LandingPageLogo = styled.img`
  width: 300px;
  max-width: 90%;
  margin-bottom: 20px;
  cursor: pointer;
`;

const messages = defineMessages({
    'landing-page-title': {
        id: 'landing-page-title',
        defaultMessage: 'Landing Page',
    },
});

type Props = {
    logout: (data: Object) => Promise<Object>,
    ws: WebSocketClient
}

const LandingPage = (props: Props) => {
    const { logout, ws } = props;
    const [errorMessage, setErrorMessage] = React.useState(null);

    const isLandingTab = useSelector(getIsLandingTab);
    const user = useSelector(getUser);
    const { apiClient } = React.useContext(APIContext);
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const history = useHistory();
    const location = useLocation();

    const defaultErrorMessage = formatMessage({ id: 'response-error' });

    React.useEffect(() => {
        dispatch(addBreadcrumbs([
            { title: formatMessage(messages['landing-page-title']) },
        ]));
        return () => {
            dispatch(removeBreadcrumb(formatMessage(messages['landing-page-title'])));
        };
    }, [formatMessage, dispatch]);

    const handleLogoutClick = (event: SyntheticMouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        if (isLandingTab) {
            ws.send(LANDING_TAB_WS_COMMAND, {
                action: 'logout',
            });
            ws.send(LANDING_TAB_WS_COMMAND, {
                action: 'unregistered_landing_tab',
            });
            window.localStorage.removeItem(LANDING_TAB_LC_KEY);
        }

        logout().then(() => {
            ws.reconnect();
            history.push('/');
            dispatch(setPermissions({}));
            dispatch(removeUser());
            dispatch(setLandingTab(null));
        });
    };

    const handleStartWork = () => {
        const normalizedLocation = normalizeToLocation(
            resolveToLocation('', location),
            location,
        );
        window.open(history.createHref(normalizedLocation), '_blank');
    };

    return (
        <>
            <Box
                background="backgroundColor"
                align="center"
                width="100%"
                height="100%"
                justify="center"
            >
                <LandingPageLogo
                    alt="Logo"
                    src={landingLogo}
                    onClick={() => {
                        if (user) {
                            handleStartWork();
                        }
                    }}
                />
                {user && (
                    <Box direction="row" gap="small">
                        <RedButton
                            label={(
                                <FormattedMessage
                                    id="account-menu.log-out"
                                    defaultMessage="Log out"
                                />
                            )}
                            onClick={handleLogoutClick}
                        />
                        <GreyButton
                            label={(
                                <FormattedMessage
                                    id="landing-page-start-work"
                                    defaultMessage="Start work"
                                />
                            )}
                            onClick={handleStartWork}
                        />
                    </Box>
                )}

                { errorMessage && (
                    <Text color="status-error" style={{ marginBottom: '10px' }}>{errorMessage}</Text>
                )}

                {!user && (
                    <Box width="medium">
                        <LoginForm redirectTo="/" />
                    </Box>
                )}
            </Box>
            <ConfirmBeforeUnload />
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    logout: {
        send: () => apiClient.deauthorize(),
    },
});

export default connectWS(connectAPI(apiCallback)(LandingPage));
